import { useEffect, useState } from 'react'
import { MapPin, Star, Bookmark } from 'lucide-react'
import type { Session } from '@supabase/supabase-js'
import { savedPostingsApi, type SavedPosting } from '../services/api'
import { reportError } from '../utils/errors'
import { deadlineCls, formatDeadline } from '../components/PostingsModal'
import PostingDetailModal from '../components/PostingDetailModal'

interface Props {
  session: Session
}

function formatPublished(value: string | null): string | null {
  if (!value) return null
  return new Date(value).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function SavedPostingsPage({ session }: Props) {
  const [items, setItems] = useState<SavedPosting[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedPosting, setSelectedPosting] = useState<SavedPosting | null>(null)

  useEffect(() => {
    savedPostingsApi.list(session)
      .then(setItems)
      .catch(e => setError(reportError(e)))
      .finally(() => setLoading(false))
  }, [])

  async function handleUnsave(postingId: string) {
    setItems(prev => prev.filter(p => p.id !== postingId))
    if (selectedPosting?.id === postingId) setSelectedPosting(null)
    await savedPostingsApi.unsave(postingId, session).catch(() => {
      savedPostingsApi.list(session).then(setItems).catch(() => {})
    })
  }

  if (loading) return <p className="p-10 text-gray-500">Caricamento…</p>

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Preferiti</h1>
        {items.length > 0 && (
          <span className="text-sm text-gray-400">{items.length} {items.length === 1 ? 'bando salvato' : 'bandi salvati'}</span>
        )}
      </div>

      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Bookmark size={48} className="text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-800 mb-1">Nessun bando salvato</h2>
          <p className="text-gray-500 text-sm max-w-xs">
            Clicca sulla stellina ★ su un bando per salvarlo qui.
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-0">
          {items.map((posting, i) => {
            const closed = posting.status === 'CLOSED'
            const published = formatPublished(posting.published_at)
            const zebra = i % 2 === 1
            const baseBg = zebra ? 'bg-slate-50' : 'bg-white'

            return (
              <li key={posting.id} className={`mb-2 border border-gray-100 rounded-xl`}>
                <div className={`flex items-start rounded-xl ${baseBg}`}>
                  {/* Main clickable area */}
                  <button
                    onClick={() => setSelectedPosting(posting)}
                    className="flex-1 min-w-0 text-left py-3.5 pl-3 pr-1 focus:outline-none"
                  >
                    <div className="min-w-0">
                      {/* Published + deadline on the same row */}
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs text-gray-400">{published ?? ''}</span>
                        {closed ? (
                          <span className="shrink-0 text-xs font-medium rounded-full px-2 py-0.5 bg-gray-100 text-gray-400">Scaduto</span>
                        ) : (
                          <span className={`shrink-0 text-xs font-medium rounded-full px-2.5 py-0.5 ${deadlineCls(posting.deadline)}`}>
                            {formatDeadline(posting.deadline)}
                          </span>
                        )}
                      </div>
                      <p className={`font-semibold text-sm leading-snug mb-1 ${closed ? 'text-gray-400' : 'text-gray-900'}`}>
                        {posting.title}
                      </p>
                      {/* Ente — own row, wraps on mobile */}
                      {posting.ente && (
                        <p className="text-xs text-gray-500 mb-1.5 leading-snug wrap-break-word">{posting.ente}</p>
                      )}
                      <div className="flex items-center justify-between gap-2">
                        {posting.locations && posting.locations.length > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-400 min-w-0">
                            <MapPin size={10} className="shrink-0" />
                            <span className="truncate">
                              {posting.locations.length > 2
                                ? `${posting.locations.slice(0, 2).join(' · ')} +${posting.locations.length - 2}`
                                : posting.locations.join(' · ')}
                            </span>
                          </span>
                        ) : <span />}
                        <span className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 bg-gray-100 text-gray-500">
                          Dettagli
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </button>

                  {/* Star button — unsave */}
                  <button
                    onClick={() => handleUnsave(posting.id)}
                    aria-label="Rimuovi dai preferiti"
                    className="shrink-0 p-2.5 pt-3.5 text-amber-400 hover:text-red-400 transition-colors"
                  >
                    <Star size={15} fill="currentColor" />
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      {selectedPosting && (
        <PostingDetailModal
          posting={selectedPosting}
          saved={true}
          onToggleSave={() => handleUnsave(selectedPosting.id)}
          onClose={() => setSelectedPosting(null)}
        />
      )}
    </div>
  )
}
